import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import API from '../../../util/api';
import ConfirmationModal from '../../Common/ConfirmationModal';
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";
import { showErrorModal } from "../../../util/general";

// ---- Styled Components ----

const Container = styled.div.attrs({ id: 'bigbox' })`
  background: #fff;
  padding: 1em;
  border-radius: 4px;
  overflow: hidden;

  h1 {
    margin-top: 0;
  }

  &::after {
    content: '';
    display: block;
    clear: both;
  }
`;

const Title = styled.h1`
 color: ${(colors) => colors.colors.labelText};
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
`;

const Table = styled.table`
  ${adminTable};
  table-layout: fixed;
  font-size: 14px;
  margin-top: 1em;

  thead th {
    ${adminTableHeaderCell};
    word-wrap: break-word;
    white-space: normal;
    padding: 0.75em 8px;
  }

  tbody td {
    ${adminTableDataCell};
    word-wrap: break-word;
    white-space: normal;
    padding: 10px 8px;
  }

  thead th:nth-child(1),
  tbody td:nth-child(1) {
    width: 22%;
  }

  thead th:nth-child(2),
  tbody td:nth-child(2) {
    width: 14%;
  }

  thead th:nth-child(3),
  tbody td:nth-child(3) {
    width: 18%;
  }

  thead th:nth-child(4),
  tbody td:nth-child(4) {
    width: 14%;
  }

  thead th:nth-child(5),
  tbody td:nth-child(5) {
    width: 10%;
  }

  thead th:nth-child(6),
  tbody td:nth-child(6) {
    width: 8%;
    text-align: center;
  }

  thead th:nth-child(7),
  tbody td:nth-child(7) {
    width: 14%;
    text-align: center;
  }
`;

const LinkButton = styled.a`
  display: block;
  text-decoration: none;
  color: ${({ colors }) => colors.buttonBackground};
  font-weight: bold;
  margin: 0;
  word-break: break-word;

  &:hover {
    text-decoration: underline;
  }

  & + & {
    margin-top: 4px;
  }
`;

const ProcessButton = styled.button`
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  border: 1px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 0.85em;

  &:hover {
    opacity: 0.9;
  }
`;

// ---- Component ----

export default function RenewalContent({ colors }) {
  const [quotes, setQuotes] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);

  useEffect(() => {
    API.getRequestedQuotes({})
      .then((resp) => {
        if (resp.status === 'Success' && Array.isArray(resp.result)) {
          setQuotes(resp.result);
        } else {
          setQuotes([]);
        }
      })
      .catch((err) => {
        console.error('Failed to load renewal quotes:', err);
        setQuotes([]);
      });
  }, []);

  const handleProcessClick = (quoteId) => {
    setSelectedQuoteId(quoteId);
    setConfirmOpen(true);
  };

  const handleConfirmProcess = async () => {
    setConfirmOpen(false);
    if (!selectedQuoteId) return;

    try {
      await API.processRequestedQuotes({ quote_id: selectedQuoteId });
      setQuotes((prev) =>
        prev.filter((q) => q.quote_id !== selectedQuoteId.toString())
      );
      setSelectedQuoteId(null);
    } catch (err) {
      showErrorModal('Processing failed: ' + err.message);
    }
  };

  const handleCancelProcess = () => {
    setConfirmOpen(false);
    setSelectedQuoteId(null);
  };

  return (
    <Container>
      <Title colors={colors}>Renewals or New Business</Title>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Name</th>
              <th>Email</th>
              <th>Requested</th>
              <th>State</th>
              <th>Renewal</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {quotes.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  There are no renewals or new business quotes at this time.
                </td>
              </tr>
            ) : (
              quotes.map((q) => (
                <tr key={q.quote_id}>
                  <td>
                    <LinkButton
                      colors={colors}
                      href={`/admincentral/quotes/detail/${q.quote_id}`}
                      title="Summary"
                    >
                      {q.org_name}
                    </LinkButton>
                    <LinkButton
                      colors={colors}
                      href={`/admincentral/quotes/pdf/${q.quote_id}`}
                      title="Download PDF"
                    >
                      PDF
                    </LinkButton>
                  </td>
                  <td>{q.contact_name}</td>
                  <td>{q.email}</td>
                  <td>{q.inserted}</td>
                  <td>{q.state}</td>
                  <td>Yes</td>
                  <td>
                    <ProcessButton
                      colors={colors}
                      onClick={() => handleProcessClick(q.quote_id)}
                    >
                      Process
                    </ProcessButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      <ConfirmationModal
        show={confirmOpen}
        title="Confirm Processing"
        message="Are you sure you want to process this quote?"
        onCancel={handleCancelProcess}
        onConfirm={handleConfirmProcess}
      />
    </Container>
  );
}
